import React, { useEffect, useRef, useState } from 'react';
import { RtModal, RtModalField } from '../ui/RtModal';

const RESEND_COOLDOWN_SECONDS = 30;
const MAX_OTP_ATTEMPTS = 5;
const LOCKOUT_DURATION_SECONDS = 10 * 60; // 10 minutes after attempts are exhausted
const OTP_EXPIRY_SECONDS = 10 * 60; // how long a sent OTP itself stays valid
const OTP_LENGTH = 6;

const formatMMSS = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Segmented OTP input: renders OTP_LENGTH single-digit boxes but stays
 * fully controlled through a single `value` string + `onChange(string)`,
 * so the parent doesn't need to change how it stores/passes otp.
 */
const OtpBoxes = ({ value, onChange, disabled, length = OTP_LENGTH, autoFocus }) => {
  const inputRefs = useRef([]);

  const digits = Array.from({ length }, (_, i) => value[i] || '');

  const focusBox = (index) => {
    const el = inputRefs.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  };

  useEffect(() => {
    if (autoFocus) {
      focusBox(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  const setValueAt = (index, char) => {
    const chars = value.split('');
    chars[index] = char;
    // Keep length capped and drop any gaps introduced beyond entered digits
    onChange(chars.join('').slice(0, length));
  };

  const handleChange = (index, event) => {
    const raw = event.target.value;
    const digitsOnly = raw.replace(/\D/g, '');

    if (!digitsOnly) {
      // User cleared the box (e.g. selected all + delete)
      setValueAt(index, '');
      return;
    }

    if (digitsOnly.length > 1) {
      // Multiple characters landed here (fast typing / IME / mobile autofill
      // suggestion) — treat like a paste starting at this box.
      distributeFrom(index, digitsOnly);
      return;
    }

    setValueAt(index, digitsOnly);
    if (index < length - 1) {
      focusBox(index + 1);
    } else {
      inputRefs.current[index]?.blur();
    }
  };

  const distributeFrom = (startIndex, rawDigits) => {
    const cleaned = rawDigits.replace(/\D/g, '').slice(0, length - startIndex);
    if (!cleaned) return;

    const chars = value.split('');
    // Ensure array is long enough to index into
    while (chars.length < length) chars.push('');

    for (let i = 0; i < cleaned.length; i += 1) {
      chars[startIndex + i] = cleaned[i];
    }

    const next = chars.join('').slice(0, length);
    onChange(next);

    const lastFilledIndex = Math.min(startIndex + cleaned.length, length) - 1;
    if (lastFilledIndex >= length - 1) {
      inputRefs.current[length - 1]?.blur();
    } else {
      focusBox(lastFilledIndex + 1);
    }
  };

  const handlePaste = (index, event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    distributeFrom(index, pasted);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        setValueAt(index, '');
      } else if (index > 0) {
        setValueAt(index - 1, '');
        focusBox(index - 1);
      }
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      setValueAt(index, '');
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusBox(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  return (
    <div className="flex justify-between gap-2 sm:gap-3">
      {digits.map((digit, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
          onFocus={(event) => event.target.select()}
          className="h-12 w-11 flex-1 rounded-xl border border-slate-300 text-center text-lg font-semibold text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 sm:h-14 sm:w-12"
          aria-label={`OTP digit ${index + 1}`}
        />
      ))}
    </div>
  );
};

const EditMobileNumberModal = ({
  isOpen,
  onClose,
  currentNumber,
  newMobileNumber,
  onMobileNumberChange,
  otp,
  onOtpChange,
  step,
  isRequestingOtp,
  isVerifyingOtp,
  onRequestOtp,
  onVerifyOtp,
  onResendOtp,
  error,
}) => {
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_OTP_ATTEMPTS);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const [otpExpiryRemaining, setOtpExpiryRemaining] = useState(0);

  const prevRequestingRef = useRef(false);
  const prevVerifyingRef = useRef(false);

  // Reset everything whenever the modal is opened fresh
  useEffect(() => {
    if (isOpen) {
      setResendCooldown(0);
      setAttemptsLeft(MAX_OTP_ATTEMPTS);
      setLockoutRemaining(0);
      setOtpExpiryRemaining(0);
    }
  }, [isOpen]);

  // Detect a successful send/resend (isRequestingOtp true -> false, no error)
  useEffect(() => {
    if (prevRequestingRef.current && !isRequestingOtp && !error) {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setAttemptsLeft(MAX_OTP_ATTEMPTS);
      setOtpExpiryRemaining(OTP_EXPIRY_SECONDS);
    }
    prevRequestingRef.current = isRequestingOtp;
  }, [isRequestingOtp, error]);

  // Detect a failed verify attempt (isVerifyingOtp true -> false, with error)
  useEffect(() => {
    if (prevVerifyingRef.current && !isVerifyingOtp && error) {
      setAttemptsLeft((prev) => Math.max(prev - 1, 0));
    }
    prevVerifyingRef.current = isVerifyingOtp;
  }, [isVerifyingOtp, error]);

  // Start the 10-minute lockout the moment attempts hit 0
  useEffect(() => {
    if (attemptsLeft === 0) {
      setLockoutRemaining(LOCKOUT_DURATION_SECONDS);
    }
  }, [attemptsLeft]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Lockout ticker — once it reaches 0, attempts are restored
  useEffect(() => {
    if (lockoutRemaining <= 0) return undefined;
    const timer = setInterval(() => {
      setLockoutRemaining((prev) => {
        if (prev <= 1) {
          setAttemptsLeft(MAX_OTP_ATTEMPTS);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lockoutRemaining]);

  // OTP expiry ticker — the sent code itself stops being valid at 0
  useEffect(() => {
    if (otpExpiryRemaining <= 0) return undefined;
    const timer = setInterval(() => {
      setOtpExpiryRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpExpiryRemaining]);

  if (!isOpen) return null;

  const isVerifyStep = step === 'verify';
  const isBusy = isRequestingOtp || isVerifyingOtp;
  const isLockedOut = lockoutRemaining > 0;
  const isExpired = isVerifyStep && otpExpiryRemaining <= 0;
  const isSendDisabled = isBusy || resendCooldown > 0 || isLockedOut;

  return (
    <RtModal
      open={isOpen}
      onClose={onClose}
      title="Edit Mobile Number"
      subtitle="Update your number securely with OTP verification."
      size="md"
      closeDisabled={isBusy}
      closeOnBackdrop={!isBusy}
      footer={
        <>
          <button
            type="button"
            className="rt-btn rt-btn--secondary"
            onClick={onClose}
            disabled={isBusy}
          >
            Cancel
          </button>

          {isVerifyStep ? (
            <>
              <button
                type="button"
                className="rt-btn rt-btn--secondary"
                onClick={onResendOtp}
                disabled={isSendDisabled}
              >
                {isRequestingOtp
                  ? 'Sending…'
                  : isLockedOut
                    ? `Resend OTP (${formatMMSS(lockoutRemaining)})`
                    : resendCooldown > 0
                      ? `Resend OTP in ${resendCooldown}s`
                      : 'Resend OTP'}
              </button>
              <button
                type="button"
                className="rt-btn rt-btn--primary"
                onClick={onVerifyOtp}
                disabled={isVerifyingOtp || otp.length !== OTP_LENGTH || isLockedOut || isExpired}
              >
                {isVerifyingOtp ? 'Verifying…' : 'Verify & Update'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="rt-btn rt-btn--primary"
              onClick={onRequestOtp}
              disabled={isSendDisabled || newMobileNumber.length !== 10}
            >
              {isRequestingOtp
                ? 'Sending OTP…'
                : isLockedOut
                  ? `Send OTP (${formatMMSS(lockoutRemaining)})`
                  : resendCooldown > 0
                    ? `Send OTP in ${resendCooldown}s`
                    : 'Send OTP'}
            </button>
          )}
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 16 }}>
        <div
          className={`rounded-xl border px-4 py-3 transition ${
            isVerifyStep ? 'border-emerald-200 bg-emerald-50' : 'border-blue-200 bg-blue-50'
          }`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                isVerifyStep ? 'bg-emerald-500 text-white' : 'bg-blue-600 text-white'
              }`}
            >
              1
            </span>
            <div>
              <p className="text-sm font-semibold text-slate-900">Enter number</p>
              <p className="text-xs text-slate-500">Request OTP</p>
            </div>
          </div>
        </div>

        <div
          className={`rounded-xl border px-4 py-3 transition ${
            isVerifyStep ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'
          }`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                isVerifyStep ? 'bg-blue-600 text-white' : 'bg-slate-300 text-slate-700'
              }`}
            >
              2
            </span>
            <div>
              <p className="text-sm font-semibold text-slate-900">Verify OTP</p>
              <p className="text-xs text-slate-500">Confirm update</p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" style={{ marginBottom: 16 }}>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Current number</p>
        <p className="mt-1 text-sm font-semibold text-slate-900">
          {currentNumber || 'Not added yet'}
        </p>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" style={{ marginBottom: 16 }}>
        <p className="text-sm font-semibold text-amber-900">OTP verification rules</p>
        <div className="mt-2 space-y-1 text-xs text-amber-800">
          <p>OTP is valid for 10 minutes from the time it is sent.</p>
          <p>You can enter the OTP a maximum of 5 times.</p>
        </div>
      </div>

      <RtModalField
        label="New Mobile Number"
        htmlFor="newMobileNumber"
        hint="We'll send a one-time password to this number."
      >
        <input
          id="newMobileNumber"
          type="text"
          value={newMobileNumber}
          onChange={(event) =>
            onMobileNumberChange(event.target.value.replace(/\D/g, '').slice(0, 10))
          }
          placeholder="Enter 10-digit mobile number"
          disabled={isVerifyStep || isBusy}
        />
      </RtModalField>

      {isVerifyStep && (
        <div style={{ marginTop: 16 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
            <p className="rt-modal__label" style={{ marginBottom: 0 }}>
              Verification OTP
            </p>
            {!isLockedOut && !isExpired && otpExpiryRemaining > 0 && (
              <p
                className={`font-mono text-xs font-medium ${
                  otpExpiryRemaining <= 60 ? 'text-red-600' : 'text-slate-500'
                }`}
              >
                Expires in {formatMMSS(otpExpiryRemaining)}
              </p>
            )}
          </div>

          <OtpBoxes
            value={otp}
            onChange={onOtpChange}
            disabled={isVerifyingOtp || isLockedOut || isExpired}
            length={OTP_LENGTH}
            autoFocus
          />

          <div className="flex items-center justify-between text-xs" style={{ marginTop: 8 }}>
            <p className="text-slate-500">
              Enter the OTP sent to{' '}
              <span className="font-semibold text-slate-700">{newMobileNumber}</span>.
            </p>
            {!isLockedOut && !isExpired && (
              <p className={attemptsLeft <= 2 ? 'font-medium text-red-600' : 'text-slate-500'}>
                {attemptsLeft} attempt{attemptsLeft === 1 ? '' : 's'} left
              </p>
            )}
          </div>
        </div>
      )}

      {isLockedOut && (
        <div
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          style={{ marginTop: 16 }}
        >
          Too many incorrect attempts. Please try again in{' '}
          <span className="font-mono font-semibold">{formatMMSS(lockoutRemaining)}</span>.
        </div>
      )}

      {!isLockedOut && isExpired && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          style={{ marginTop: 16 }}
        >
          This OTP has expired. Please resend to get a new code.
        </div>
      )}

      {error ? (
        <div
          className="truncate rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          style={{ marginTop: 16 }}
          title={error}
        >
          {error}
        </div>
      ) : null}
    </RtModal>
  );
};

export default EditMobileNumberModal;
